// src/api/authFetch.js
// Shared auth plumbing for every authed call to the BACKEND (not the proxy).
//
// Two responsibilities, both centralized so client.js and harClient.js behave
// identically and any future authed client can opt in with one import:
//
//   1. handleAuthResponse(resp) — sliding session. The backend re-issues a fresh
//      JWT in the `X-Refreshed-Token` response header once a token passes half
//      its lifetime (see backend/index.js issueRefreshedTokenIfStale). We read it
//      off ANY authed response and silently persist it to localStorage.auth_token
//      — the only place the token is ever stored. An actively-used session keeps
//      sliding forward and never reaches the 8h expiry wall.
//
//   2. notifyAuthExpired() — graceful 401. When the backend rejects a token
//      (expired/invalid) we DON'T hard-reload the SPA (that tears down the live
//      WebSocket + captured-traffic dashboard). Instead we clear only the auth
//      credentials and dispatch an `auth:expired` window event; App.js shows an
//      in-app re-login overlay ON TOP of the still-mounted dashboard, so the
//      captured session (TrafficContext / groupedResults.slim) survives untouched.

export const AUTH_EXPIRED_EVENT = "auth:expired";

/** Bearer auth header if a token exists, else {}. Single source of truth so both
 *  api clients attach the token the same way. */
export function authHeader() {
    try {
        const token = localStorage.getItem("auth_token");
        return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
        return {};
    }
}

/** Read a rotated token off a backend response and persist it. Safe to call on
 *  every authed response (no-op when the header is absent). */
export function handleAuthResponse(resp) {
    try {
        const fresh = resp && resp.headers && resp.headers.get("X-Refreshed-Token");
        if (fresh) localStorage.setItem("auth_token", fresh);
    } catch {
        /* header unreadable (e.g. CORS not exposing it) — ignore, token stays as-is */
    }
    return resp;
}

/** Clear ONLY auth credentials and signal the app to show a re-login overlay.
 *  Deliberately does NOT touch sessionId / groupedResults / any dashboard state —
 *  the live capture must survive a re-auth. */
export function notifyAuthExpired() {
    try {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("auth_user");
    } catch { /* ignore */ }
    try {
        window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
    } catch { /* non-browser env */ }
}

/** True if an error/status looks like a backend auth rejection. */
export function isAuthError(statusOrMessage) {
    if (typeof statusOrMessage === "number") return statusOrMessage === 401;
    const s = String(statusOrMessage || "");
    return s.includes("401") || s.includes("invalid_token") || s.includes("unauthorized") || s.includes("no_token");
}
